import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Button, Divider, Drawer, Typography, Autocomplete, TextField, makeStyles } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core';
import { AudacyFlashLogo, BeasleyLogo, CreativeLoveLogo, CashByCreativeLogo, CCCLogo, SpecByteLogo } from '../logos';
import { FilterBuilder, WhereBuilder } from '@loopback/filter';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import useAuth from '../../hooks/useAuth';
import useAPI from '../../hooks/useAPI';
import NavSection from '../NavSection'
import useSettings from '../../hooks/useSettings';

const useStyles = makeStyles((theme) => ({
  marketSelect: {
    backgroundColor: '#fff',
    borderRadius: 5
  }
}))

const loggedOutSections = [
  {
    items: [
      {
        title: 'Home',
        path: '/'
      },
      {
        title: 'Need Help?',
        path: '/help'
      },
    ]
  }
]

const getAccountChildren = (site) => {
  switch (site) {
    case 'ccc':
      return [
        {
          title: 'My Audio Specs',
          path: '/account/myspecs/audio'
        },
        {
          title: 'My Banner Specs',
          path: '/account/myspecs/digital'
        },
        {
          title: 'Full Productions',
          path: '/account/concierge/fullproductions'
        },
        {
          title: 'Spanish - Full Production',
          path: '/account/concierge/spanishfullproduction'
        },
        {
          title: 'Dry Voices',
          path: '/account/concierge/dryvoice'
        },
        {
          title: 'Campaign - Audio',
          path: '/account/concierge/audio'
        },
        {
          title: 'Campaign - Digital',
          path: '/account/concierge/digital'
        },
        {
          title: 'Jingles',
          path: '/account/concierge/jingle'
        },
        {
          title: 'My Advertisers',
          path: '/account/advertisers'
        },
        {
          title: 'Old CCC Campaigns',
          path: '/account/specials'
        },
        {
          title: 'Monitor',
          path: '/account/monitor'
        },
        {
          title: 'Favorites',
          path: '/account/favorites'
        },
        {
          title: 'Manage Users',
          path: '/account/manage-users'
        },
        {
          title: 'Account Info',
          path: '/account/accountinfo'
        },
        {
          title: 'Reports',
          path: '/account/reports'
        }
      ];
    case 'cbc':
      return [
        {
          title: 'My Specs',
          path: '/account/myspecs'
        },
        {
          title: 'Banners',
          path: '/account/banners'
        },
        {
          title: 'PreRolls',
          path: '/account/prerolls'
        },
        {
          title: 'Concierge',
          path: '/account/concierge'
        },
        {
          title: 'Customs',
          path: '/account/customs'
        },
        {
          title: 'Monitor',
          path: '/account/monitor'
        },
        {
          title: 'Account Info',
          path: '/account/accountinfo'
        },
        {
          title: 'Reports',
          path: '/account/reports'
        }
      ];
    default:
      return [
        {
          title: 'My Specs',
          path: '/account/myspecs'
        },
        {
          title: 'Concierge',
          path: '/account/concierge'
        },
        {
          title: 'Monitor',
          path: '/account/monitor'
        },
        {
          title: 'Banners',
          path: '/account/banners'
        },
        {
          title: 'PreRolls',
          path: '/account/prerolls'
        },
        {
          title: 'Demands',
          path: '/account/demands'
        },
        {
          title: 'Favorites',
          path: '/account/favorites'
        },
        {
          title: 'Manage Users',
          path: '/account/manage-users'
        },
        {
          title: 'Account Info',
          path: '/account/accountinfo'
        }
      ];
  }
};

const getSections = ({ themeName, themeSite, canAccessAdmin }) => ([
  {
    items: [
      ...(themeName !== 'CCC' ? [{
        title: 'Concierge',
        path: '/concierge'
      }] : []),
      {
        title: 'My Account',
        path: '/account',
        children: getAccountChildren(themeSite)
      },
      ...(canAccessAdmin ? [{
        title: 'Site Admin',
        path: '/admin'
      }] : []),
      {
        title: 'Need Help?',
        path: '/help'
      }
    ]
  }
]);

const FrontendSidebar = ({ onMobileClose, openMobile, markets }) => {
  const theme = useTheme()
  const { api } = useAPI()
  const { settings, setMarket } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const classes = useStyles()
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const { user, isAuthenticated, isCompanyWideUser, isMarketWideUser, isProducer, isSiteAdmin, logout } = useAuth()
  const [selectedMarket, setSelectedMarket] = useState()
  const [specialsCount, setSpecialsCount] = useState(0)
  const baseSections = getSections({
    themeName: theme.name,
    themeSite: theme.site,
    canAccessAdmin: isSiteAdmin || isProducer
  });
  const sections = baseSections.map((section) => ({
    ...section,
    items: section.items.map((item) => {
      if (!item.children) return item;

      let children = [...item.children];

      if (children.some((child) => child.path === '/account/specials') && specialsCount === 0) {
        children = children.filter((child) => child.path !== '/account/specials');
      }

      if (children.some((child) => child.path === '/account/monitor') && !(isSiteAdmin || isCompanyWideUser || isMarketWideUser)) {
        children = children.filter((child) => child.path !== '/account/monitor');
      }

      if (children.some((child) => child.path === '/account/reports') && !(isSiteAdmin || isCompanyWideUser)) {
        children = children.filter((child) => child.path !== '/account/reports');
      }

      return {
        ...item,
        children
      };
    })
  }));

  const handleChangeMarket = (event, newValue) => {
    if (!newValue) return

    setSelectedMarket(newValue)
    setMarket(newValue)
  }

  useEffect(() => {
    if (selectedMarket !== undefined) { return }
    if (settings.market === undefined && user !== undefined) {
      const found = markets.find(market => market.id === Number.parseInt(user.marketId))
      if (found) {
        setSelectedMarket(found)
        setMarket(found)
      }
    } else {
      setSelectedMarket(settings.market)
    }
  }, [user, markets])

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  useEffect(() => {
    const getSpecialsCount = async () => {
      if (!isAuthenticated || !settings.market?.id || !user?.email) {
        setSpecialsCount(0);
        return;
      }

      try {
        const where = new WhereBuilder().and({ marketId: settings.market.id });

        if (!isMarketWideUser) {
          where.and({ createdBy: user.email });
        }

        const filter = new FilterBuilder().where(where.where);
        const response = await api.CustomSpotRequest.count(filter.build());

        if (response.error) {
          throw response.error;
        }

        setSpecialsCount(response.count);
      } catch (err) {
        console.error(err);
        setSpecialsCount(0);
      }
    };

    getSpecialsCount();
  }, [api, isAuthenticated, isMarketWideUser, settings.market?.id, user?.email]);

  const handleLogout = async () => {
    try {
      const response = await logout()
      if (response) {
        navigate('/')
      }
    } catch (err) {
      console.error(err)
      toast.error('Unable to logout')
    }
  }

  return (
    <Drawer
      anchor="left"
      onClose={onMobileClose}
      open={!lgUp && openMobile}
      variant="temporary"
      PaperProps={{
        sx: {
          backgroundColor: 'background.default',
          width: {
            xs: '75vw',
            sm: '60vw',
            md: '50vw',
            lg: '30vw'
          },
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          p: 0
        }}
      >
        <RouterLink to="/">
          {(() => {
            switch (theme.name) {
              case 'SPECbyte':
                return <SpecByteLogo width="180px" sx={{ display: { md: 'inline', xs: 'none' }, }} />;
              case 'CCC':
                return <CCCLogo width="180px" sx={{ display: { md: 'inline', xs: 'none' }, }} />;
              case 'CashByCreative':
                return <CashByCreativeLogo width="170px" sx={{ display: { md: 'inline', xs: 'none' }, }} />;
              case 'Beasley':
                return <BeasleyLogo width="90px" sx={{ display: { md: 'inline', xs: 'none' }, }} />;
              default:
                return null
            }
          })()}
        </RouterLink>
        <Box sx={{ mt: 0, width: '100%'}}>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.primary.main, textAlign: 'center' }}
          >
            {isAuthenticated ? `Hello, ${user.firstName}` : 'Welcome, please login.'}
          </Typography>

        </Box>
        <Box sx={{ display: 'flex', mx: 2, flexDirection: 'column' }}>
          {isAuthenticated && markets.length > 1 ? (
            <Autocomplete
              getOptionLabel={(market) => {
                const company = market.company?.name;
                return company ? `${market.name}: ${company}` : (market.name || '');
              }}
              options={markets}
              value={selectedMarket || null}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              autoComplete
              size='small'
              onChange={handleChangeMarket}
              renderInput={(params) => (
                <TextField
                  className={classes.marketSelect}
                  fullWidth
                  name="market"
                  label="Select Market"
                  variant="outlined"
                  {...params}
                  sx={{ mt: 2, backgroundColor: 'background.paper' }}
                />
              )}
            />
          ) : null}
        </Box>
        <Divider orientation='horizontal' sx={{ width: '100%', color: theme.palette.primary.main, my: 2 }} />
        { isAuthenticated 
          ? sections.map((section, idx) => (
            <NavSection
              key={idx}
              pathname={location.pathname}
              {...section}
            />
          )) 
          : loggedOutSections.map((section, idx) => (
            <NavSection
              key={idx}
              pathname={location.pathname}
              {...section}
            />
          ))
        }
        <Box sx={{ m: 2, display: 'flex', flexDirection: 'column', pb: 5 }}>
          { isAuthenticated 
            ? <Button
              color="primary"
              component="a"
              onClick={handleLogout}
              size="medium"
              variant="contained"
            >
              Log Out
            </Button> 
            : <Button
              color="primary"
              component={RouterLink}
              to="/authentication/login"
              size="medium"
              variant="contained"
            >
              Log In
            </Button>
          }
        </Box>
      </Box>
    </Drawer>
  );
};

FrontendSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default FrontendSidebar;
